import React, { createContext, useContext, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const InstallContext = createContext(null);

const parseInstallError = (rawError) => {
    if (!rawError) return 'Installation failed';

    if (rawError.includes('size exceeds limit')) {
        const modelMatches = rawError.match(/filename=([^,]+\.safetensors)/g);
        if (modelMatches && modelMatches.length > 0) {
            const models = modelMatches.map(m => m.replace('filename=', '')).join(', ');
            return `Insufficient disk space to download required models: ${models}. Please free up space and try again.`;
        }
        return 'Insufficient disk space to download required models. Please free up space and try again.';
    }

    if (rawError.includes("not installed")) {
        const match = rawError.match(/'([^']+)' not installed/);
        const cosyflowName = match ? match[1] : 'Cosyflow';
        return `"${cosyflowName}" could not be installed. Please check your disk space and server logs.`;
    }

    return rawError;
};

export function InstallProvider({ children }) {
    const pendingInstalls = useRef(new Map());

    const isInstalling = useCallback((cosyflowUid) => {
        for (const [, data] of pendingInstalls.current) {
            if (data.cosyflowUid === cosyflowUid) {
                return true;
            }
        }
        return false;
    }, []);

    const startInstall = useCallback(async (cosyflow, serverUrl) => {
        if (isInstalling(cosyflow.uid)) {
            Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                showCloseButton: true,
                background: '#FFFFFF',
                color: '#190A4E',
            }).fire({
                icon: 'warning',
                title: `"${cosyflow.title}" is already being installed...`,
            });
            return null;
        }

        const installId = `${cosyflow.uid}-${Date.now()}`;

        Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            showCloseButton: true,
            background: '#FFFFFF',
            color: '#190A4E',
        }).fire({
            icon: 'info',
            title: `Installing "${cosyflow.title}" locally...`,
        });

        const cosyflowData = typeof cosyflow.cosyflow === 'string'
            ? JSON.parse(cosyflow.cosyflow)
            : cosyflow.cosyflow;

        const controller = new AbortController();
        const installPromise = axios.post(
            `${serverUrl}/api/cosy/install-local`,
            { cosyflow: cosyflowData },
            {
                headers: { 'Content-Type': 'application/json' },
                timeout: 1800000,
                signal: controller.signal
            }
        );

        pendingInstalls.current.set(installId, {
            cosyflowUid: cosyflow.uid,
            cosyflowTitle: cosyflow.title,
            startTime: Date.now(),
            promise: installPromise,
            controller: controller
        });

        installPromise
            .then((response) => {
                Swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    showCloseButton: true,
                    background: '#FFFFFF',
                    color: '#190A4E',
                }).fire({
                    icon: 'success',
                    title: `"${cosyflow.title}" installed successfully!`,
                });
                pendingInstalls.current.delete(installId);
            })
            .catch((error) => {
                let errorMessage = 'Installation failed';
                let errorIcon = 'error';

                if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                    errorMessage = 'Installation timed out after 30 minutes. The server may still be processing - please check your local server status.';
                    errorIcon = 'warning';
                } else if (error.name === 'AbortError' || error.name === 'CanceledError') {
                    errorMessage = 'Installation was cancelled.';
                    errorIcon = 'info';
                } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
                    errorMessage = 'Could not connect to server. Please ensure your local server is running.';
                } else if (error.response?.status >= 400) {
                    const rawError = error.response?.data?.error || error.response?.data?.message;
                    errorMessage = rawError ? parseInstallError(rawError) : `Server error (${error.response.status}). Please check server logs.`;
                } else if (error.response?.data?.error) {
                    errorMessage = parseInstallError(error.response.data.error);
                } else if (error.response?.data?.message) {
                    errorMessage = parseInstallError(error.response.data.message);
                } else if (error.message) {
                    errorMessage = error.message;
                }

                console.error(`[InstallContext] Installation failed for "${cosyflow.title}":`, error);

                Swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    showCloseButton: true,
                    width: 600,
                    background: '#FFFFFF',
                    color: '#190A4E',
                }).fire({
                    icon: errorIcon,
                    title: `Failed to install "${cosyflow.title}"`,
                    text: errorMessage,
                });
                pendingInstalls.current.delete(installId);
            });

        return installId;
    }, [isInstalling]);

    const getPendingInstalls = useCallback(() => {
        return Array.from(pendingInstalls.current.entries()).map(([id, data]) => ({
            id,
            cosyflowTitle: data.cosyflowTitle,
            startTime: data.startTime,
        }));
    }, []);

    return (
        <InstallContext.Provider value={useMemo(() => ({ startInstall, getPendingInstalls, isInstalling }), [startInstall, getPendingInstalls, isInstalling])}>
            {children}
        </InstallContext.Provider>
    );
}

export function useInstallContext() {
    const context = useContext(InstallContext);
    if (!context) {
        throw new Error('useInstallContext must be used within an InstallProvider');
    }
    return context;
}

export default InstallContext;
